/**
 * The legend, which MUST swap with the view toggle.
 *
 * The two views encode different things: levelled colours by COMPOSITION (what the week's
 * hours are spent on), as-scheduled by RATIO (how far past capacity the week is). A legend
 * that carried both would be teaching a reading that is only ever half true — "85–100%" does
 * not exist in the levelled view and "Still queued" does not exist in the scheduled one.
 *
 * `where` picks the last entry: the full-size chart draws the backlog burn-down as a line,
 * the small multiples draw the same fact as the dashed ghost above the capacity rule.
 */

import React from 'react'

interface Item {
  label: string
  hatch?: boolean
  background?: string
  border?: string
  rule?: boolean
}

//: The clearing swatch is a RAMP, not a colour: red says "behind", and its depth says how
//: late the work being cleared is (format.ts ageRed). One flat swatch would describe the
//: encoding as if it had one value.
const CLEARING_RAMP =
  'linear-gradient(90deg,' +
  ' color-mix(in srgb, var(--lc-hatch-a) 90%, transparent),' +
  ' color-mix(in srgb, var(--lc-hatch-a) 22%, transparent))'

const LEVELLED: Item[] = [
  { label: 'Due this week', background: 'var(--lc-teal)' },
  { label: 'Clearing backlog · older ▸ newer', background: CLEARING_RAMP },
  { label: 'Free', background: 'var(--lc-free)' },
]

const SCHEDULED: Item[] = [
  { label: 'Under 85%', background: 'var(--lc-teal)' },
  { label: '85–100%', background: 'var(--lc-amber)' },
  { label: 'Over 100%', background: 'var(--lc-red)' },
  { label: 'Overdue backlog · folded into week 1', hatch: true },
]

const QUEUED: Item = {
  label: 'Still queued',
  background: 'var(--lc-ghost-fill)',
  border: '1px dashed var(--lc-ghost-line)',
}
const REMAINING: Item = { label: 'Backlog remaining', rule: true }

function Swatch({ item }: { item: Item }): React.JSX.Element {
  if (item.rule) {
    return <span className="block h-[2px] w-4 flex-none" style={{ background: 'var(--lc-red)' }} />
  }
  return (
    <span
      className={`block h-[11px] w-[11px] flex-none rounded-[2px] ${item.hatch ? 'lc-hatch' : ''}`}
      style={{ background: item.background, border: item.border, boxSizing: 'border-box' }}
    />
  )
}

export default function Legend({ levelled, where }: {
  levelled: boolean
  where: 'overview' | 'detail'
}): React.JSX.Element {
  const items = levelled ? [...LEVELLED, where === 'detail' ? REMAINING : QUEUED] : SCHEDULED
  return (
    <div className={`flex flex-wrap items-center ${where === 'detail' ? 'gap-[18px]' : 'gap-[14px]'}`}>
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-1.5">
          <Swatch item={item} />
          <span className="font-mono text-[10.5px] font-medium" style={{ color: 'var(--lc-ink3)' }}>
            {item.label}
          </span>
        </div>
      ))}
    </div>
  )
}
